import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { settingsApi } from '../lib/api';

// Google OAuth Configuration
const GOOGLE_OAUTH_URL =
  process.env.REACT_APP_GOOGLE_OAUTH_URL ||
  '/api/auth/oauth/start';

const resolveBackendOrigin = () => {
  const rawBackendUrl = process.env.REACT_APP_BACKEND_URL;
  if (rawBackendUrl) {
    try {
      return new URL(rawBackendUrl, window.location.origin).origin;
    } catch (e) {
      console.warn('Invalid REACT_APP_BACKEND_URL, falling back to window origin', e);
    }
  }
  return window.location.origin;
};

const normalizeOAuthBase = (value) => {
  const trimmed = (value || '').trim();
  const httpIndex = trimmed.indexOf('http');
  // Recover from accidental prefixes like "e3https://..."
  return httpIndex > 0 ? trimmed.slice(httpIndex) : trimmed;
};

const buildGoogleOAuthUrl = ({ base, redirectUri, frontendBase }) => {
  const normalizedBase = normalizeOAuthBase(base) || '/api/auth/oauth/start';

  let url;
  try {
    url = new URL(normalizedBase, window.location.origin);
  } catch {
    // Fall back to local backend start endpoint.
    url = new URL('/api/auth/oauth/start', window.location.origin);
  }

  // If someone provides only a host base (e.g. https://auth.example.com), add the expected path in code.
  // Supported broker base forms:
  // - https://host                 -> /auth/v1/env/oauth/google
  // - https://host/auth/v1/env     -> /auth/v1/env/oauth/google
  // - https://host/.../oauth/google (already complete)
  const isLocalStart = url.pathname.endsWith('/api/auth/oauth/start');
  const looksLikeJustHost = !isLocalStart && (url.pathname === '' || url.pathname === '/');
  if (!isLocalStart && looksLikeJustHost) {
    url.pathname = '/auth/v1/env/oauth/google';
  } else if (!isLocalStart && url.pathname.endsWith('/auth/v1/env')) {
    url.pathname = `${url.pathname}/oauth/google`;
  }

  // Always redirect back into the backend callback (it forwards into /login with session_id).
  url.searchParams.set('redirect_uri', redirectUri);
  if (frontendBase && url.pathname.endsWith('/auth/oauth/start')) {
    url.searchParams.set('frontend_base', frontendBase);
  }

  // If bypassing the local backend start endpoint, ensure required params exist.
  if (!url.pathname.endsWith('/api/auth/oauth/start')) {
    if (!url.searchParams.get('response_type')) url.searchParams.set('response_type', 'session_id');
    if (!url.searchParams.get('scope')) url.searchParams.set('scope', 'email profile');
  }

  return url.toString();
};

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [orgSettings, setOrgSettings] = useState(null);
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Fetch organization settings for logo and company name
  useEffect(() => {
    const fetchOrgSettings = async () => {
      try {
        const response = await settingsApi.getPublic();
        setOrgSettings(response.data);
      } catch (error) {
        console.error('Failed to fetch org settings:', error);
      }
    };
    fetchOrgSettings();
  }, []);

  // Handle Google OAuth callback
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      handleGoogleCallback(sessionId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Surface backend error codes from OAuth redirect
  useEffect(() => {
    const error = searchParams.get('error');
    if (error) {
      const messages = {
        google_oauth_not_configured: 'Google login is not configured. Please contact admin.',
        oauth_state_missing: 'Google login session is invalid. Please try again.',
        oauth_state_invalid: 'Google login session expired. Please try again.',
        google_oauth_no_access_token: 'Google login failed: no access token returned.',
        google_oauth_exchange_failed: 'Could not complete Google login. Please try again.',
        google_oauth_no_email: 'Google login did not return an email address.',
        google_oauth_domain_forbidden: 'Your email domain is not allowed for Google login.',
        user_not_found: 'Your account is not yet set up in the portal. Contact admin.',
        oauth_no_session: 'Google login session not found. Please try again.',
      };
      toast.error(messages[error] || 'Google sign-in failed. Please try again.');
    }
  }, [searchParams]);

  const handleGoogleCallback = async (sessionId) => {
    setGoogleLoading(true);
    try {
      await loginWithGoogle(sessionId);
      toast.success('Welcome! You have been signed in with Google.');
      navigate('/dashboard');
    } catch (error) {
      console.error('Google OAuth error:', error);
      toast.error(error.response?.data?.detail || 'Failed to sign in with Google');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const frontendBase = window.location.origin;
    const backendOrigin = resolveBackendOrigin();
    const callbackUrl = new URL('/auth/google/callback', backendOrigin);
    setGoogleLoading(true);
    window.location.href = buildGoogleOAuthUrl({
      base: GOOGLE_OAUTH_URL,
      redirectUri: callbackUrl.toString(),
      frontendBase,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}!`);
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  if (googleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-lg font-medium">Signing in with Google...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Hero Image */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-secondary">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1758691736975-9f7f643d178e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBvZmZpY2UlMjBkaXZlcnNlJTIwdGVhbSUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NjgyMjY3Nzd8MA&ixlib=rb-4.1.0&q=85)',
          }}
        >
          <div className="absolute inset-0 bg-secondary/80" />
        </div>
        <div className="relative z-10 flex flex-col justify-center items-center text-white p-12">
          <h1 className="text-4xl font-bold mb-4">Performance Portal</h1>
          <p className="text-lg text-white/80 text-center max-w-md">
            Streamline your performance management process with our comprehensive performance portal.
          </p>
          <div className="mt-12 grid grid-cols-2 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold">4</div>
              <div className="text-sm text-white/70">Review Stages</div>
            </div>
            <div>
              <div className="text-3xl font-bold">10</div>
              <div className="text-sm text-white/70">Core Competencies</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="text-center">
            {/* Company Logo */}
            <div className="mx-auto mb-2 w-20 h-20 rounded-lg flex items-center justify-center overflow-hidden">
              {orgSettings?.logo_url ? (
                <img 
                  src={orgSettings.logo_url} 
                  alt={orgSettings?.company_name || 'Company Logo'} 
                  className="w-full h-full object-contain"
                  data-testid="company-logo"
                />
              ) : (
                <div className="w-full h-full bg-primary flex items-center justify-center">
                  <span className="text-2xl font-bold text-primary-foreground">PP</span>
                </div>
              )}
            </div>
            {/* Company Name */}
            {orgSettings?.company_name && (
              <p className="text-sm font-semibold text-muted-foreground mb-2" data-testid="company-name">
                {orgSettings.company_name}
              </p>
            )}
            <CardTitle className="text-2xl">Welcome Back</CardTitle>
            <CardDescription>Sign in to your account to continue</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Username</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  disabled={loading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
              </div>
            </div>

            {/* Google Login Button */}
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleGoogleLogin}
              disabled={loading || googleLoading}
              data-testid="google-login-btn"
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Sign in with Google
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
